/**
 * Roth ordering rules and the conversion ladder (spec §7 extension).
 *
 * A Roth balance is not one pot. Money comes out in a fixed statutory order,
 * and each layer has its own treatment:
 *
 *   1. CONTRIBUTIONS — always out first, tax-free and penalty-free at any age.
 *   2. CONVERSIONS — FIFO by vintage. The money was already taxed when it was
 *      converted, so it is never taxed again; but withdrawn within FIVE YEARS
 *      of its conversion, while under 59.5, it carries the 10% penalty. Each
 *      conversion runs its own clock. This is exactly the mechanism behind the
 *      "Roth conversion ladder": convert this year, wait five, spend it
 *      penalty-free decades before 59.5.
 *   3. EARNINGS — last out. Before 59.5 they are ordinary income AND penalized.
 *
 * Earnings are DERIVED (balance − contributions − unspent conversions) rather
 * than stored, so investment growth lands in the earnings layer automatically
 * and the layers can never disagree with the balance.
 *
 * Simplifications, stated honestly:
 *  - At 59.5+ everything is treated as qualified. Strictly a Roth IRA also
 *    needs the account itself to be 5 years old; someone retiring on a Roth
 *    balance has essentially always cleared that.
 *  - Roth 401(k)/403(b) money follows these same IRA ordering rules here.
 *    In-plan the real rules pro-rate every distribution, which is why the
 *    standard move is to roll to a Roth IRA at separation and pick up the
 *    ordering rules — what this models.
 */

export interface RothConversionVintage {
  /** Calendar year the conversion happened — starts that layer's 5-year clock. */
  year: number;
  /** Portion that was taxable at conversion (the penalty applies to this). */
  taxableAmount: number;
}

export interface RothLayers {
  /** Direct contributions still in the account. */
  contributions: number;
  /** Conversions still in the account, oldest first. */
  conversions: RothConversionVintage[];
}

export interface RothDraw {
  /** Withdrawn free of both tax and penalty. */
  taxFree: number;
  /** Non-qualified earnings — ordinary income. */
  ordinaryIncome: number;
  /** 10% early-withdrawal penalty incurred. */
  penalty: number;
  /** Layers remaining after the draw (the caller stores this). */
  remaining: RothLayers;
  /** Conversion dollars withdrawn before their 5-year clock ran out. */
  unseasonedConversionsUsed: number;
}

export const AGE_59_5 = 59.5;
export const SEASONING_YEARS = 5;

export function emptyLayers(): RothLayers {
  return { contributions: 0, conversions: [] };
}

/** Layers for an account as configured, defaulting to "all earnings". */
export function layersFromAccount(a: {
  rothContributionBasis?: number;
  rothConversionVintages?: { year: number; taxableAmount: number }[];
  balance: number;
}): RothLayers {
  const conversions = (a.rothConversionVintages ?? [])
    .map((v) => ({ year: v.year, taxableAmount: Math.max(0, v.taxableAmount) }))
    .sort((x, y) => x.year - y.year);
  return {
    contributions: Math.max(0, a.rothContributionBasis ?? 0),
    conversions,
  };
}

export function totalConversions(layers: RothLayers): number {
  return layers.conversions.reduce((t, c) => t + c.taxableAmount, 0);
}

/**
 * Earnings are whatever the balance holds beyond the tracked layers. Clamped
 * at zero: a balance can fall below its own contribution basis after a market
 * drop, and that does not make earnings negative.
 */
export function earningsIn(layers: RothLayers, balance: number): number {
  return Math.max(0, balance - layers.contributions - totalConversions(layers));
}

/** Add a conversion done in `year`, keeping vintages oldest-first. */
export function addConversion(
  layers: RothLayers,
  year: number,
  taxableAmount: number,
): RothLayers {
  if (taxableAmount <= 0) return layers;
  const conversions = [...layers.conversions, { year, taxableAmount }];
  conversions.sort((x, y) => x.year - y.year);
  return { contributions: layers.contributions, conversions };
}

/**
 * Withdraw `amount` from a Roth account, consuming layers in statutory order.
 *
 * @param balance  the account balance BEFORE this withdrawal
 * @param year     the withdrawal year (against each vintage's 5-year clock)
 * @param ownerAge the owner's age this year
 * @param penaltyExempt the 10% is waived by another rule entirely (Rule of 55,
 *        457(b) after separation, inherited). Earnings are still ORDINARY
 *        INCOME — these rules waive the penalty, not the tax.
 */
export function drawFromRoth(
  layers: RothLayers,
  balance: number,
  amount: number,
  year: number,
  ownerAge: number,
  penaltyExempt = false,
): RothDraw {
  const qualified = ownerAge >= AGE_59_5;
  const penaltyFree = qualified || penaltyExempt;
  let remaining = Math.max(0, Math.min(amount, balance));

  let taxFree = 0;
  let ordinaryIncome = 0;
  let penalty = 0;
  let unseasonedConversionsUsed = 0;

  // 1. Contributions: free at any age.
  const fromContributions = Math.min(remaining, layers.contributions);
  taxFree += fromContributions;
  remaining -= fromContributions;
  const contributions = layers.contributions - fromContributions;

  // 2. Conversions, oldest vintage first.
  const conversions: RothConversionVintage[] = [];
  for (const v of layers.conversions) {
    if (remaining <= 0) {
      conversions.push(v);
      continue;
    }
    const take = Math.min(remaining, v.taxableAmount);
    remaining -= take;
    taxFree += take; // already taxed at conversion — never taxed twice
    const seasoned = year - v.year >= SEASONING_YEARS;
    if (!seasoned && !penaltyFree) {
      penalty += take * 0.1;
      unseasonedConversionsUsed += take;
    }
    const left = v.taxableAmount - take;
    if (left > 0) conversions.push({ year: v.year, taxableAmount: left });
  }

  // 3. Earnings last: taxable and penalized unless qualified.
  if (remaining > 0) {
    if (qualified) {
      taxFree += remaining;
    } else {
      ordinaryIncome += remaining;
      if (!penaltyExempt) penalty += remaining * 0.1;
    }
    remaining = 0;
  }

  return {
    taxFree,
    ordinaryIncome,
    penalty,
    remaining: { contributions, conversions },
    unseasonedConversionsUsed,
  };
}

/**
 * How much could be taken from this account right now with NO penalty — the
 * number that decides whether an early retiree's ladder actually reaches.
 */
export function penaltyFreeAmount(
  layers: RothLayers,
  balance: number,
  year: number,
  ownerAge: number,
  penaltyExempt = false,
): number {
  if (ownerAge >= AGE_59_5 || penaltyExempt) return balance;
  const seasoned = layers.conversions
    .filter((v) => year - v.year >= SEASONING_YEARS)
    .reduce((t, v) => t + v.taxableAmount, 0);
  return Math.min(balance, layers.contributions + seasoned);
}
